/**
 * The sort protocol and the identity rendering behind the dashboard's "Top
 * users" card, kept out of the component so they can be reasoned about — and
 * run — on their own.
 *
 * No imports, deliberately, on the precedent `model-usage.ts` and
 * `analytics-window.ts` set: Node 24 strips types, so a module with no imports
 * and no JSX runs directly under `node analytics-users.ts`, and the self-check
 * at the foot of this file is what `pnpm --filter @empite/admin-portal test`
 * executes. The row shapes are declared structurally rather than imported from
 * admin-client for that reason; they are subsets of `AnalyticsTopUser`, so the
 * real documents are assignable to them and the compiler still checks the join.
 *
 * Everything here exists because it would fail SILENTLY if it drifted:
 *
 *   * a sort the caller is not allowed to ask for. `cost`, `revenue` and
 *     `margin` are refused with a 403 for an operator without `wallets:read`,
 *     because a ranking BY money is the money in ordinal form. The card must
 *     not be able to send one — a table that 403s when a heading is clicked
 *     teaches an operator that the dashboard is broken.
 *   * a heading whose arrow says one thing and whose accessible name says
 *     another. `aria-sort` belongs on the `<th>`, which the card cannot reach
 *     (the `DataTable` primitive owns it), so the state travels in the
 *     button's own name and an arrow alone would be decoration.
 *   * an identity column that is EMPTY rather than withheld. Absent because
 *     the operator may not see it, absent because the account never had one,
 *     and present are three renders, not two.
 */

/** What the API will rank by. The same union as `AnalyticsUserSort` in
 *  admin-client, re-declared so this module stays import-free. */
export type TopUserSort = "requests" | "tokens" | "cost" | "revenue" | "margin" | "last_seen";

export type SortOrder = "asc" | "desc";

/** A resolved ordering: what the card sends, and what the response echoes
 *  back. Never null — unlike the models screen there is no catalogue order to
 *  fall back to, so "unsorted" is not one of the states a leaderboard has. */
export interface UserSort {
  sort: TopUserSort;
  order: SortOrder;
}

/**
 * The server's own default, mirrored so the card can label its heading before
 * the first response arrives.
 *
 * `tokens` and not `requests`: a leaderboard exists to find spend, and one
 * account streaming a million tokens costs more than a thousand accounts
 * asking one question each.
 */
export const DEFAULT_USER_SORT: UserSort = { sort: "tokens", order: "desc" };

/**
 * The three sorts that ARE the money.
 *
 * Mirrors `analyticsUserMoneySorts` in internal/admin/analytics_users_http.go.
 * The server is still the control — it re-checks and answers 403 — and this
 * only stops the card offering a heading that cannot be clicked.
 */
export const MONEY_SORTS: TopUserSort[] = ["cost", "revenue", "margin"];

export function isMoneySort(sort: TopUserSort): boolean {
  return MONEY_SORTS.indexOf(sort) !== -1;
}

/** Whether this operator may ask for this ordering at all. */
export function sortAllowed(sort: TopUserSort, moneyVisible: boolean): boolean {
  return moneyVisible || !isMoneySort(sort);
}

/**
 * What clicking a heading means.
 *
 * A new column starts DESCENDING, on the models screen's reasoning: every
 * question a leaderboard column is opened for ("who is costing the most",
 * "who is busiest") is a question about the top of the list. Clicking the
 * active column flips it, and there is no third click — see `UserSort`.
 */
export function nextUserSort(current: UserSort, column: TopUserSort): UserSort {
  if (current.sort !== column) {
    return { sort: column, order: "desc" };
  }
  return { sort: column, order: current.order === "desc" ? "asc" : "desc" };
}

/**
 * The ordering actually sent, given what this operator may see.
 *
 * A money sort held in the card's state while `money_visible` is false would
 * be a request the server refuses, and the refusal would arrive as a broken
 * table rather than as an explanation. It cannot normally happen — the money
 * headings are not rendered at all without the grant — but the two facts
 * arrive at different times: the operator's permission list is on screen
 * first, and the response's `money_visible` is the authority that corrects it.
 * A card that guessed wrong for one render must fall back, not 403.
 */
export function resolveUserSort(current: UserSort, moneyVisible: boolean): UserSort {
  return sortAllowed(current.sort, moneyVisible) ? current : DEFAULT_USER_SORT;
}

/** Which columns a caller may sort by, in the order they appear in the table.
 *  The money three are ABSENT for an operator without the grant — the columns
 *  they would order are absent too, so there is nothing to head. */
export function userSortColumns(moneyVisible: boolean): TopUserSort[] {
  const all: TopUserSort[] = ["requests", "tokens", "cost", "revenue", "margin", "last_seen"];
  return all.filter((sort) => sortAllowed(sort, moneyVisible));
}

/** The direction a column is currently sorted in, or null when the table is
 *  ordered by some other column. */
export function activeOrder(sort: UserSort, column: TopUserSort): SortOrder | null {
  return sort.sort === column ? sort.order : null;
}

/**
 * The heading's accessible name, which carries the sort state because the
 * `<th>` that would carry `aria-sort` is inside the shared primitive.
 *
 * `last_seen` gets its own words: "highest first" over a column of timestamps
 * is a sentence a reader has to translate, and the translation is where they
 * get it backwards. Everything else is a quantity and reads as one.
 */
export function sortStateLabel(column: TopUserSort, active: SortOrder | null): string {
  if (active === null) {
    return "not sorted";
  }
  if (column === "last_seen") {
    return active === "desc" ? "sorted, most recent first" : "sorted, longest ago first";
  }
  return active === "desc" ? "sorted, highest first" : "sorted, lowest first";
}

/** The glyph beside the label. Decoration — it is `aria-hidden`, and
 *  `sortStateLabel` is what actually says this. */
export function sortArrow(active: SortOrder | null): string {
  return active === "desc" ? "▼" : active === "asc" ? "▲" : "⇅";
}

/** One leaderboard row, cut down to the identity fields this module reads.
 *  `email` and `display_name` are ABSENT — not blank — without `users:read`. */
export interface IdentityRow {
  user_id: string;
  email?: string;
  display_name?: string;
}

/**
 * How to render the account column, which is THREE things and not two.
 *
 * `withheld` is "this operator may not see who this is" and names the grant
 * that would show it. `unnamed` is "this account has no name and no email on
 * file", which is a fact about the account rather than about the reader, and
 * is what a device-only registration looks like. Rendering either as an empty
 * cell would read as a page that failed to load, and rendering the first as
 * the second would send an operator hunting for a broken account.
 *
 * Every variant still carries the id, because every row links to the user
 * detail page — `user_id` is not gated, and the leaderboard would be useless
 * if it were: the whole point is to get from a spike to the account behind it.
 */
export type RowIdentity =
  | { kind: "named"; name: string; email: string | null }
  | { kind: "unnamed" }
  | { kind: "withheld" };

export function rowIdentity(row: IdentityRow, identityVisible: boolean): RowIdentity {
  if (!identityVisible) {
    return { kind: "withheld" };
  }
  // Trimmed, because a display name of spaces is not a name — it would render
  // as a blank link with nothing to click on the words.
  const name = (row.display_name ?? "").trim();
  const email = (row.email ?? "").trim();
  if (name !== "") {
    return { kind: "named", name, email: email === "" ? null : email };
  }
  if (email !== "") {
    // The email IS the name here rather than being repeated underneath it: two
    // lines holding the same string is a row that looks like it lost one.
    return { kind: "named", name: email, email: null };
  }
  return { kind: "unnamed" };
}

/** The page sizes the card offers. The server's cap is 200 and it CLAMPS
 *  rather than refusing, so nothing here can produce a moved bound; the
 *  response's own `limit` is still what the table is labelled from. */
export const ROW_OPTIONS: number[] = [10, 25, 50, 100, 200];

/** The card's default. Ten rows is a dashboard card; two hundred is a report,
 *  and the select is how an operator asks for one. */
export const DEFAULT_ROWS = 10;

/* -------------------------------------------------------------------------- *
 * Self-check. `node analytics-users.ts`.
 * -------------------------------------------------------------------------- */

function show(value: unknown): string {
  return JSON.stringify(value, (_key, held: unknown) =>
    held === undefined ? "<undefined>" : held,
  );
}

let passed = 0;

function check(name: string, actual: unknown, expected: unknown): void {
  const a = show(actual);
  const b = show(expected);
  if (a !== b) {
    throw new Error(`analytics-users: ${name}: expected ${b}, got ${a}`);
  }
  passed += 1;
}

function selfCheck(): void {
  // Which sorts are the money.
  check("cost is a money sort", isMoneySort("cost"), true);
  check("revenue is a money sort", isMoneySort("revenue"), true);
  check("margin is a money sort", isMoneySort("margin"), true);
  check("tokens is not", isMoneySort("tokens"), false);
  check("requests is not", isMoneySort("requests"), false);
  check("last_seen is not", isMoneySort("last_seen"), false);

  // The whitelist and the offered headings agree, in both directions. A sort
  // added to one and not the other is either a heading that 403s or a column
  // that cannot be ordered, and neither reports as an error anywhere.
  check("every sort is offered with money", userSortColumns(true), [
    "requests",
    "tokens",
    "cost",
    "revenue",
    "margin",
    "last_seen",
  ]);
  check("the money three are withdrawn without it", userSortColumns(false), [
    "requests",
    "tokens",
    "last_seen",
  ]);

  check("a money sort needs the grant", sortAllowed("margin", false), false);
  check("a money sort with the grant", sortAllowed("margin", true), true);
  check("a plain sort never does", sortAllowed("last_seen", false), true);

  // The toggle.
  check("a new column starts at the top", nextUserSort(DEFAULT_USER_SORT, "requests"), {
    sort: "requests",
    order: "desc",
  });
  check("the active column flips down to up", nextUserSort(DEFAULT_USER_SORT, "tokens"), {
    sort: "tokens",
    order: "asc",
  });
  check(
    "and back again — there is no third click",
    nextUserSort({ sort: "tokens", order: "asc" }, "tokens"),
    { sort: "tokens", order: "desc" },
  );
  check(
    "a new column starts at the top even from an ascending one",
    nextUserSort({ sort: "tokens", order: "asc" }, "last_seen"),
    { sort: "last_seen", order: "desc" },
  );

  // The 403 the card must never earn.
  check(
    "a money sort falls back when money turns out to be withheld",
    resolveUserSort({ sort: "revenue", order: "asc" }, false),
    DEFAULT_USER_SORT,
  );
  check(
    "and is sent untouched when it is not",
    resolveUserSort({ sort: "revenue", order: "asc" }, true),
    { sort: "revenue", order: "asc" },
  );
  check(
    "a plain sort is never rewritten",
    resolveUserSort({ sort: "requests", order: "asc" }, false),
    { sort: "requests", order: "asc" },
  );

  // The heading's state, in words.
  check(
    "the sorted column, descending",
    sortStateLabel("requests", "desc"),
    "sorted, highest first",
  );
  check("the sorted column, ascending", sortStateLabel("requests", "asc"), "sorted, lowest first");
  check("an unsorted column", sortStateLabel("requests", null), "not sorted");
  check("a time column says so", sortStateLabel("last_seen", "desc"), "sorted, most recent first");
  check(
    "a time column, ascending",
    sortStateLabel("last_seen", "asc"),
    "sorted, longest ago first",
  );
  check("an unsorted time column", sortStateLabel("last_seen", null), "not sorted");

  check("the active column", activeOrder({ sort: "tokens", order: "asc" }, "tokens"), "asc");
  check("an inactive one", activeOrder({ sort: "tokens", order: "asc" }, "requests"), null);

  check("the arrow follows the state", sortArrow("desc"), "▼");
  check("the ascending arrow", sortArrow("asc"), "▲");
  check("the idle arrow", sortArrow(null), "⇅");

  // Identity, in its three renders.
  const full = { user_id: "u1", email: "ada@example.com", display_name: "Ada" };
  check("a named account", rowIdentity(full, true), {
    kind: "named",
    name: "Ada",
    email: "ada@example.com",
  });
  check("withheld beats named", rowIdentity(full, false), { kind: "withheld" });
  check(
    "an account with no name falls back to the email",
    rowIdentity({ user_id: "u1", email: "ada@example.com" }, true),
    {
      kind: "named",
      name: "ada@example.com",
      // Not repeated underneath itself.
      email: null,
    },
  );
  check("an account with neither", rowIdentity({ user_id: "u1" }, true), {
    kind: "unnamed",
  });
  check(
    "a blank display name is not a name",
    rowIdentity({ user_id: "u1", display_name: "   ", email: "ada@example.com" }, true),
    { kind: "named", name: "ada@example.com", email: null },
  );
  check(
    "a blank email is not an email",
    rowIdentity({ user_id: "u1", display_name: "Ada", email: "  " }, true),
    { kind: "named", name: "Ada", email: null },
  );
  check(
    "two blanks are an unnamed account, not a named blank",
    rowIdentity({ user_id: "u1", display_name: " ", email: " " }, true),
    { kind: "unnamed" },
  );

  // The page sizes stay inside the server's cap, or the card would ask for a
  // number the answer silently moves.
  check("the largest page is the server's cap", Math.max(...ROW_OPTIONS), 200);
  check("the smallest is a real page", Math.min(...ROW_OPTIONS), 10);
  check("the default is offered", ROW_OPTIONS.indexOf(DEFAULT_ROWS) !== -1, true);

  console.log(`analytics-users self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
